import { MonetizationOnOutlined } from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Box,
  Button,
  Card,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography
} from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { useChurchStore } from "features/churches";
import moment from "moment";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExportIncomeStatement, useIncomeStatement } from "../api";
import { EthiopianDateField } from "./EthiopianDateField";

export const IncomeStatement = () => {
  const [startDate, setStartDate] = useQueryState(
    "startDate",
    parseAsString.withDefault(moment().startOf("month").format("YYYY-MM-DD"))
  );
  const [endDate, setEndDate] = useQueryState(
    "endDate",
    parseAsString.withDefault(moment().format("YYYY-MM-DD"))
  );
  const [filterDates, setFilterDates] = useState({ startDate, endDate });

  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const navigate = useNavigate();

  const { data: incomeStatement, isLoading } = useIncomeStatement({
    churchId: selectedChurch?.id,
    startDate: filterDates.startDate,
    endDate: filterDates.endDate
  });

  const { mutate: exportIncomeStatement, isLoading: isExporting } =
    useExportIncomeStatement();

  const handleFilter = () => {
    setFilterDates({ startDate, endDate });
  };

  const summaryData = {
    totalRevenue:
      Number(incomeStatement?.totals?.total_income).toLocaleString() || 0,
    totalExpense:
      Number(incomeStatement?.totals?.total_expense).toLocaleString() || 0,
    netIncome: Number(incomeStatement?.totals?.net_income).toLocaleString() || 0
  };

  const incomeStatementData = {
    revenue: incomeStatement?.data?.income || [],
    expenses: incomeStatement?.data?.expense || []
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          Income Statement report
        </Typography>
        <Box>
          <IconButton
            onClick={() =>
              exportIncomeStatement({
                churchId: selectedChurch?.id,
                queries: {
                  startDate: filterDates.startDate,
                  endDate: filterDates.endDate
                }
              })
            }
            disabled={isExporting}
          >
            <FileDownloadIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3, width: "50%" }}>
        <EthiopianDateField
          size="small"
          label="Start date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
        />
        <EthiopianDateField
          size="small"
          label="End date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilter}
          sx={{ padding: "8px 32px", borderRadius: "12px" }}
        >
          Filter
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <Card elevation={0} sx={{ flex: 1, p: 2, backgroundColor: "#FAFAFA" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography color="textSecondary">Total Revenue</Typography>
            <MonetizationOnOutlined color="action" sx={{ color: "#00A76F" }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {summaryData.totalRevenue} ETB
          </Typography>
        </Card>
        <Card elevation={0} sx={{ flex: 1, p: 2, backgroundColor: "#FAFAFA" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography color="textSecondary">Total Expense</Typography>
            <MonetizationOnOutlined color="action" sx={{ color: "#FF5630" }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {summaryData.totalExpense} ETB
          </Typography>
        </Card>
        <Card elevation={0} sx={{ flex: 1, p: 2, backgroundColor: "#FAFAFA" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography color="textSecondary">Net Income</Typography>
            <MonetizationOnOutlined color="action" sx={{ color: "#0C53B7" }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {summaryData.netIncome} ETB
          </Typography>
        </Card>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #E0E0E0",
          borderRadius: "12px",
          p: 3
        }}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell colSpan={2}>
                <Typography variant="h6" textAlign="center" fontWeight={700}>
                  Income Statement
                </Typography>
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell colSpan={2}>
                <Typography fontWeight="bold" sx={{ color: "text.secondary" }}>
                  Revenue
                </Typography>
              </TableCell>
            </TableRow>

            {incomeStatementData.revenue.map(item => (
              <TableRow key={item.id}>
                <TableCell
                  onClick={() => {
                    navigate(
                      `/finances/temp/chart-of-accounts/${
                        item.id
                      }?accountName=${item.account_name}&accountNumber=${
                        item.account_number
                      }&accountType=${item.account_type || ""}`
                    );
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" }
                  }}
                >
                  {item.account_name}
                </TableCell>
                <TableCell align="right">
                  {Number(item.total).toLocaleString()} ETB
                </TableCell>
              </TableRow>
            ))}

            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Total Revenue</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {summaryData.totalRevenue} ETB
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell colSpan={2}>
                <Typography
                  fontWeight="bold"
                  sx={{ color: "text.secondary", mt: 2 }}
                >
                  Expenses
                </Typography>
              </TableCell>
            </TableRow>

            {incomeStatementData.expenses.map(item => (
              <TableRow key={item.id}>
                <TableCell
                  onClick={() => {
                    navigate(
                      `/finances/temp/chart-of-accounts/${
                        item.id
                      }?accountName=${item.account_name}&accountNumber=${
                        item.account_number
                      }&accountType=${item.account_type || ""}`
                    );
                  }}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" }
                  }}
                >
                  {item.account_name}
                </TableCell>
                <TableCell align="right">
                  {Number(item.total).toLocaleString()} ETB
                </TableCell>
              </TableRow>
            ))}

            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Total Expense</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {summaryData.totalExpense} ETB
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: "bold", color: "#0C53B7" }}>
                Net Income
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: "bold", color: "#0C53B7" }}
              >
                {summaryData.netIncome} ETB
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
